import React from "react";
import { Building2, Mail, Phone, Calendar, Tag } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import Link from "next/link";
import type { BaseEntity, SimpleTableConfig } from "../../UniversalCRMTable/types";
import { getStatusColor, getPriorityColor, getStatusIcon } from "../utils/status-utils";
import { getStringProperty } from "@/utils/entity-type-guards";
import { HeaderFieldItem } from "./DetailHeaderContent/HeaderFieldItem";
import { HeaderTags } from "./DetailHeaderContent/HeaderTags";
import { useUniversalAuthOptional } from "@west-tennessee-consulting/ui";
import { DetailHeaderModelView } from "../header/DetailHeaderModelView";
import { getDetailHeaderModel, getDetailHeaderModelFields } from "../header/registry";
import type { DetailHeaderEntityData } from "../utils/build-detail-header-entity-data";

interface DetailHeaderContentProps<T extends BaseEntity> {
  item: T;
  config: SimpleTableConfig<T>;
  pageTitle: string;
  pageDescription: string;
  entityData: DetailHeaderEntityData;
  onTagsUpdate?: (newTags: string[]) => Promise<void>;
  /** When provided, header fields with a `fieldId` are editable in place. */
  onInlineEdit?: (item: T) => Promise<void>;
}

function DetailHeaderContentComponent<T extends BaseEntity>({
  item,
  config,
  pageTitle,
  pageDescription,
  entityData,
  onTagsUpdate,
  onInlineEdit,
}: DetailHeaderContentProps<T>) {
  // Same edit gate as the inline panel: editing disabled for the config and for
  // client users (read-only header). Uses the NON-throwing auth variant so this
  // presentational header stays renderable outside the provider tree (unit
  // tests, boundaries) — no provider → treated as non-client, but editing only
  // engages when an `onInlineEdit` is actually wired in.
  const { appUser } = useUniversalAuthOptional();
  const roleName =
    (appUser as { role?: string | null; roles?: { name?: string | null } | null } | null)?.roles
      ?.name ??
    (appUser as { role?: string | null } | null)?.role ??
    null;
  const canEdit = config.actions?.edit !== false && roleName !== "client_user";
  const statusColor = entityData.status ? getStatusColor(entityData.status, entityData.entityType) : null;
  const statusIcon = entityData.status ? getStatusIcon(entityData.status, entityData.entityType) : null;
  const priorityColor = entityData.priority ? getPriorityColor(entityData.priority) : null;

  const itemAsRecord = item as Record<string, unknown>;
  const headerEntityContext = React.useMemo(
    () => ({ item: itemAsRecord, entityType: config.entity }),
    [itemAsRecord, config.entity]
  );
  const companyName = getStringProperty(itemAsRecord, "company_name");
  const title = getStringProperty(itemAsRecord, "title");
  const companyId = getStringProperty(itemAsRecord, "company_id");

  // Typed, codegen-derived header model (subtitle + facts + metrics) for
  // registered entities; unregistered entities keep the legacy generic path.
  const headerModel = React.useMemo(
    () => getDetailHeaderModel(config.entity, item),
    [config.entity, item]
  );

  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-start gap-2 md:gap-4 mb-2 md:mb-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-lg md:text-3xl lg:text-4xl font-bold text-card-foreground mb-1 md:mb-2 break-words line-clamp-2 md:line-clamp-none">
            {pageTitle}
          </h1>

          {headerModel ? (
            <DetailHeaderModelView
              model={headerModel}
              fallbackDescription={pageDescription}
              item={item}
              config={config}
              onInlineEdit={onInlineEdit}
              canEdit={canEdit}
            />
          ) : (
          <div className="space-y-2">
            {config.entity === "contacts" && companyName ? (
              <div className="text-gray-600 dark:text-gray-400 text-base md:text-lg flex flex-wrap items-center gap-2">
                {title && <span>{title}</span>}
                {title && companyName && <span>•</span>}
                {companyName && companyId ? (
                  <Link
                    href={`/companies/${companyId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {companyName}
                  </Link>
                ) : (
                  companyName && <span>{companyName}</span>
                )}
              </div>
            ) : (
              <div className="text-gray-600 dark:text-gray-400 text-base md:text-lg">{pageDescription}</div>
            )}

            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
              {entityData.fields.company && config.entity !== "contacts" && (
                <HeaderFieldItem icon={Building2} value={String(entityData.fields.company)} />
              )}
              {entityData.fields.email && (
                <HeaderFieldItem
                  icon={Mail}
                  value={String(entityData.fields.email)}
                  isEmail
                  entityContext={headerEntityContext}
                />
              )}
              {entityData.fields.phone && (
                <HeaderFieldItem
                  icon={Phone}
                  value={String(entityData.fields.phone)}
                  isPhone
                  entityContext={headerEntityContext}
                />
              )}
              {entityData.fields.ticketNumber && (
                <HeaderFieldItem icon={Tag} value={String(entityData.fields.ticketNumber)} />
              )}
              {entityData.fields.dueDate && (
                <HeaderFieldItem
                  icon={Calendar}
                  value={new Date(String(entityData.fields.dueDate)).toLocaleDateString()}
                />
              )}
            </div>
          </div>
          )}
        </div>

        <div className="flex flex-row md:flex-col gap-1 md:gap-2 flex-shrink-0 flex-wrap justify-end">
          {entityData.status && (
            <Badge
              variant="outline"
              className={`text-xs md:text-sm font-medium px-2 md:px-3 py-0.5 md:py-1 ${statusColor} flex items-center gap-1 whitespace-nowrap`}
            >
              {statusIcon}
              {entityData.status}
            </Badge>
          )}

          {entityData.priority && priorityColor && (
            <Badge
              variant="outline"
              className={`text-xs md:text-sm font-medium px-2 md:px-3 py-0.5 md:py-1 ${priorityColor} whitespace-nowrap`}
            >
              {entityData.priority}
            </Badge>
          )}
        </div>
      </div>

      <HeaderTags
        entityData={entityData}
        itemAsRecord={itemAsRecord}
        config={config}
        onTagsUpdate={onTagsUpdate}
      />
    </div>
  );
}

function areDetailHeaderContentPropsEqual<T extends BaseEntity>(
  prevProps: DetailHeaderContentProps<T>,
  nextProps: DetailHeaderContentProps<T>
): boolean {
  if (prevProps.pageTitle !== nextProps.pageTitle || prevProps.pageDescription !== nextProps.pageDescription) {
    return false;
  }
  if (prevProps.config !== nextProps.config) {
    return false;
  }
  if (prevProps.onTagsUpdate !== nextProps.onTagsUpdate) {
    return false;
  }
  if (prevProps.onInlineEdit !== nextProps.onInlineEdit) {
    return false;
  }
  if (prevProps.item?.id !== nextProps.item?.id) {
    return false;
  }

  const prevEntityData = prevProps.entityData;
  const nextEntityData = nextProps.entityData;

  if (JSON.stringify(prevEntityData.tags) !== JSON.stringify(nextEntityData.tags)) {
    return false;
  }
  if (prevEntityData.status !== nextEntityData.status) {
    return false;
  }
  if (prevEntityData.priority !== nextEntityData.priority) {
    return false;
  }
  if (prevEntityData.entityType !== nextEntityData.entityType) {
    return false;
  }

  const displayedFieldKeys = [
    "company",
    "email",
    "phone",
    "title",
    "ticketNumber",
    "category",
    "assignedTo",
    "dueDate",
    "role",
    "department",
  ] as const;

  for (const fieldKey of displayedFieldKeys) {
    const prevValue = prevEntityData.fields[fieldKey];
    const nextValue = nextEntityData.fields[fieldKey];

    if (prevValue !== nextValue) {
      return false;
    }
  }

  // Header-model entities read additional item fields (revenue, hours, …);
  // compare those too so a changed metric re-renders the header.
  const prevItemRecord = prevProps.item as Record<string, unknown>;
  const nextItemRecord = nextProps.item as Record<string, unknown>;
  for (const fieldKey of getDetailHeaderModelFields(nextProps.config.entity)) {
    if (prevItemRecord[fieldKey] !== nextItemRecord[fieldKey]) {
      return false;
    }
  }

  return true;
}

export const DetailHeaderContent = React.memo(
  DetailHeaderContentComponent,
  areDetailHeaderContentPropsEqual
) as typeof DetailHeaderContentComponent;
